/**
 * DeviceSection — Наряд бүртгэхэд ТОНОГ ТӨХӨӨРӨМЖ сонгож, технологийн карт
 * ба ХАБЭА-н зааврыг АВТОМАТААР бөглөх хэсэг.
 *
 * Энэ бол HRT хуучин системийн хамгийн үнэ цэнтэй шийдэл: ажилтан ТТ-өө
 * сонгоход систем 4,762 холбоосоос харгалзах баримтуудыг олж өгнө.
 * Зөвшөөрлийн хуудасны 3.1 «ХАБ-ын заавар, журмын дугаар: 2а, 19, 35ш…»
 * ба 3.2 «Технологийн карт» хэсэг ингэж бөглөгддөг.
 *
 * Ажилтан саналыг ХАСАХ боломжтой (шаардлагагүй бол), гараар нэмэх нь
 * лавлахаас хайж сонгоно. `is_auto` тэмдэглэгээгээр систем санал болгосон
 * эсэхийг ялгаж хадгална — автомат саналын чанарыг хэмжихэд хэрэгтэй.
 *
 * Backend: POST /xab/hr-suggest · GET /xab/hr-devices
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Select, Tag, Tooltip, Spin, Alert, Input, Button, message } from "antd";
import {
  PaperClipOutlined,
  CloseOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import { xabHrRefApi } from "src/api/xabApi";

const PRIMARY = "#0F6E56";
const trim = (v) => (typeof v === "string" ? v.trim() : v);

/** Нэг баримтын мөр — дугаар, тайлбар, хугацаа, хавсралт, хасах товч */
function DocRow({ row, no, onRemove, readonly }) {
  const expired = row.is_expired;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 6,
        padding: "4px 0",
        borderTop: "1px solid #F1F2F4",
      }}
    >
      {/* Хавсралттай бол дарж нээнэ — ажилтан зааврыг эндээсээ уншина */}
      {row.has_file ? (
        <Tooltip title={`Нээх: ${row.file_name || "баримт"}`}>
          <a href={row.file_url} target="_blank" rel="noreferrer">
            <PaperClipOutlined style={{ color: "#0EA5E9" }} />
          </a>
        </Tooltip>
      ) : (
        <Tooltip title="Баримт хавсаргаагүй">
          <PaperClipOutlined style={{ color: "#DDE1E6" }} />
        </Tooltip>
      )}
      <b style={{ minWidth: 46 }}>{trim(no)}</b>
      {/* Систем санал болгосон уу, ажилтан гараар нэмсэн үү */}
      {row.is_auto === false ? (
        <Tooltip title="Гараар нэмсэн">
          <Tag style={{ margin: 0, fontSize: 9 }}>гар</Tag>
        </Tooltip>
      ) : null}
      <span style={{ flex: 1, minWidth: 0, color: "#5B6472", fontSize: 11.5 }}>
        {row.description || "—"}
        {row.group_name ? (
          <Tag style={{ margin: "0 0 0 4px", fontSize: 9 }}>{row.group_name}</Tag>
        ) : null}
      </span>
      {expired ? (
        <Tag color="red" style={{ margin: 0, fontSize: 10 }}>
          хугацаа дууссан
        </Tag>
      ) : null}
      {!readonly && (
        <Tooltip title="Хасах">
          <CloseOutlined
            onClick={() => onRemove(row)}
            style={{ color: "#C4C9D1", cursor: "pointer", fontSize: 11 }}
          />
        </Tooltip>
      )}
    </div>
  );
}

/**
 * AddByNo — ЗААВАР / КАРТЫГ ДУГААРААР хайж нэмэх.
 *
 * Автомат санал нь тоног төхөөрөмжөөс гардаг. Гэхдээ ажилтан нэмэлт заавар
 * иш татах шаардлага гарч болно (ажлын онцлогоос хамаарч). Тэр үед дугаарыг
 * нь бичихэд лавлахаас хайж АГУУЛГЫГ нь харуулна — юу нэмж байгаагаа
 * баталгаажуулаад жагсаалтад `is_auto=false` гэж тэмдэглэн орно.
 */
function AddByNo({ kind, onAdd, disabled }) {
  const [no, setNo] = useState("");
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const search = () => {
    const v = String(no || "").trim();
    if (!v) return;
    setBusy(true);
    setErr("");
    setFound(null);
    xabHrRefApi
      .byNo(v, kind)
      .then((r) => {
        if (r?.found) setFound(r.item);
        else setErr(r?.message || "Олдсонгүй");
      })
      .catch(() => setErr("Хайхад алдаа гарлаа"))
      .finally(() => setBusy(false));
  };

  if (disabled) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <Input
          size="small"
          style={{ width: 140 }}
          placeholder={kind === "card" ? "Картын №" : "Зааврын № (35ш)"}
          value={no}
          onChange={(e) => setNo(e.target.value)}
          onPressEnter={search}
        />
        <Button
          size="small"
          icon={<SearchOutlined />}
          loading={busy}
          onClick={search}
          disabled={!no}
        >
          Хайх
        </Button>
        {found && (
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={() => {
              onAdd(found);
              setNo("");
              setFound(null);
            }}
          >
            Нэмэх
          </Button>
        )}
      </div>

      {/* Олдсон бол АГУУЛГЫГ нь харуулж баталгаажуулна */}
      {found && (
        <div
          style={{
            marginTop: 4,
            padding: "6px 8px",
            background: "#fff",
            border: "1px solid #E6E9EF",
            borderRadius: 6,
            fontSize: 11.5,
          }}
        >
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <b>{trim(found.instruction_no || found.card_no)}</b>
            {found.group_name ? (
              <Tag style={{ margin: 0, fontSize: 10 }}>{found.group_name}</Tag>
            ) : null}
            {found.clause_no ? (
              <span style={{ color: "#8A94A6" }}>заалт {found.clause_no}</span>
            ) : null}
            {found.is_expired ? (
              <Tag color="red" style={{ margin: 0, fontSize: 10 }}>
                хугацаа дууссан
              </Tag>
            ) : found.extended_date ? (
              <Tag color="green" style={{ margin: 0, fontSize: 10 }}>
                {found.extended_date}
              </Tag>
            ) : null}
            {found.has_file ? (
              <Tooltip title={`Нээх: ${found.file_name || "баримт"}`}>
                <a href={found.file_url} target="_blank" rel="noreferrer">
                  <PaperClipOutlined style={{ color: "#0EA5E9" }} />
                </a>
              </Tooltip>
            ) : null}
          </div>
          <div style={{ color: "#5B6472", marginTop: 2 }}>
            {found.description || "—"}
          </div>
          {found.content ? (
            <div
              style={{
                color: "#8A94A6",
                marginTop: 3,
                maxHeight: 72,
                overflowY: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {found.content}
            </div>
          ) : null}
        </div>
      )}
      {err ? (
        <div style={{ fontSize: 11, color: "#DC2626", marginTop: 3 }}>{err}</div>
      ) : null}
    </div>
  );
}

export default function DeviceSection({
  value,
  onChange,
  unitCode,
  readonly,
  // ⚠ Нарядын АЖЛЫН ТӨРӨЛ — баримтыг шүүхэд. Нэг ТТ дээр цахилгаан,
  //   механик, засвар гэх мэт өөр ажил хийгддэг тул төрөл бүрд өөр
  //   заавар/карт мөрдөгдөнө. Хоосон бол бүх баримт ирнэ.
  jobTypeIds = [],
}) {
  // value: { devices: [], cards: [], safeties: [] }
  // ⚠ `|| []` нь рендэр бүрд ШИНЭ массив үүсгэдэг тул useCallback/useMemo-ийн
  //   хамаарал байнга солигдоно. Тиймээс useMemo-оор тогтворжуулна.
  const devices = useMemo(() => value?.devices || [], [value?.devices]);
  const cards = useMemo(() => value?.cards || [], [value?.cards]);
  const safeties = useMemo(() => value?.safeties || [], [value?.safeties]);

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  // Тоног төхөөрөмжийн жагсаалт — БН сонгосон бол тухайн цехийнхээр шүүнэ
  const fetchDevices = useCallback(
    (q) => {
      setLoading(true);
      xabHrRefApi
        .devices({ q: q || undefined, unit_code: unitCode || undefined, limit: 300 })
        .then((r) => setOptions(r?.items || []))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    },
    [unitCode],
  );

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  /** ТТ солигдоход систем карт/зааврыг дахин санал болгоно.
   *  Ажилтны ГАРААР нэмсэн мөрийг (is_auto=false) хадгална — дарж бичихгүй. */
  const applySuggest = useCallback(
    (nextDevices) => {
      const ids = nextDevices.map((d) => d.device_legacy_id).filter(Boolean);
      if (!ids.length) {
        onChange({ devices: nextDevices, cards: [], safeties: [] });
        return;
      }
      setSuggesting(true);
      xabHrRefApi
        // ⚠ Ажлын төрлөөр ШҮҮНЭ: нэг ТТ дээр цахилгаан, механик, засвар
        //   гэх мэт өөр ажил хийгддэг бөгөөд тус бүр өөрийн заавар/карттай.
        //   Төрөл сонгоогүй бол бүх баримт ирнэ (хуучин зан төлөв).
        .suggest(ids, { job_type_ids: jobTypeIds, bn_code: unitCode })
        .then((r) => {
          const manualCards = cards.filter((c) => c.is_auto === false);
          const manualSafe = safeties.filter((s) => s.is_auto === false);
          const autoCards = (r.cards || []).map((c) => ({
            ...c,
            card_legacy_id: c.legacy_id,
            is_auto: true,
          }));
          const autoSafe = (r.instructions || []).map((s) => ({
            ...s,
            safety_legacy_id: s.legacy_id,
            is_auto: true,
          }));
          // Гараар нэмсэнтэй давхардвал автоматыг үлдээнэ
          const seenC = new Set(autoCards.map((c) => c.card_legacy_id));
          const seenS = new Set(autoSafe.map((s) => s.safety_legacy_id));
          onChange({
            devices: nextDevices,
            cards: [
              ...autoCards,
              ...manualCards.filter((c) => !seenC.has(c.card_legacy_id)),
            ],
            safeties: [
              ...autoSafe,
              ...manualSafe.filter((s) => !seenS.has(s.safety_legacy_id)),
            ],
          });
        })
        .catch(() => onChange({ ...value, devices: nextDevices }))
        .finally(() => setSuggesting(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, safeties, onChange, value, jobTypeIds, unitCode],
  );

  const handleDeviceChange = (ids) => {
    const next = ids.map((id) => {
      const o = options.find((x) => String(x.legacy_id) === String(id));
      return {
        device_legacy_id: Number(id),
        device_name: o?.title || "",
      };
    });
    applySuggest(next);
  };

  const expiredCount = useMemo(
    () => [...cards, ...safeties].filter((r) => r.is_expired).length,
    [cards, safeties],
  );

  return (
    <div>
      <div className="flex items-center">
        <span className="w-1/3 font-semibold">Тоног төхөөрөмж:</span>
        <Select
          mode="multiple"
          showSearch
          allowClear
          disabled={readonly}
          className="w-full text-xs"
          placeholder="Ажил хийгдэх тоног төхөөрөмжөө сонгоно уу"
          loading={loading}
          filterOption={false}
          onSearch={fetchDevices}
          value={devices.map((d) => String(d.device_legacy_id))}
          onChange={handleDeviceChange}
          options={options.map((o) => ({
            value: String(o.legacy_id),
            label: o.unit_code ? `${o.title} — ${o.unit_code}` : o.title,
          }))}
        />
      </div>

      {devices.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {suggesting ? (
            <div style={{ textAlign: "center", padding: 12 }}>
              <Spin size="small" />
              <span style={{ marginLeft: 8, fontSize: 12, color: "#8A94A6" }}>
                Баримт хайж байна…
              </span>
            </div>
          ) : (
            <>
              {expiredCount > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-2"
                  message={`${expiredCount} баримтын хүчинтэй хугацаа дууссан`}
                  description="ХАБЭА хэлтэс лавлахаа шинэчлэх шаардлагатай. Наряд олгоход тэмдэглэгдэнэ."
                />
              )}

              <div
                style={{
                  background: "#FAFBFC",
                  border: "1px solid #E6E9EF",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                <div style={{ fontSize: 11.5, fontWeight: 700, color: PRIMARY }}>
                  Технологийн карт ({cards.length})
                  <span style={{ fontWeight: 400, color: "#9AA3B0" }}>
                    {" "}— системээс автоматаар
                  </span>
                </div>
                {cards.length ? (
                  cards.map((c) => (
                    <DocRow
                      key={c.card_legacy_id}
                      row={c}
                      no={c.card_no}
                      readonly={readonly}
                      onRemove={(x) =>
                        onChange({
                          ...value,
                          cards: cards.filter(
                            (y) => y.card_legacy_id !== x.card_legacy_id,
                          ),
                        })
                      }
                    />
                  ))
                ) : (
                  <div style={{ fontSize: 11.5, color: "#A8AFBC", padding: "4px 0" }}>
                    Холбогдсон карт байхгүй
                  </div>
                )}
                <AddByNo
                  kind="card"
                  disabled={readonly}
                  onAdd={(row) => {
                    if (cards.some((c) => c.card_legacy_id === row.legacy_id)) {
                      message.info("Аль хэдийн нэмэгдсэн байна");
                      return;
                    }
                    onChange({
                      ...value,
                      cards: [
                        ...cards,
                        { ...row, card_legacy_id: row.legacy_id, is_auto: false },
                      ],
                    });
                  }}
                />

                <div
                  style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: PRIMARY,
                    marginTop: 8,
                  }}
                >
                  ХАБЭА-н заавар ({safeties.length})
                </div>
                {safeties.length ? (
                  safeties.map((s) => (
                    <DocRow
                      key={s.safety_legacy_id}
                      row={s}
                      no={s.instruction_no}
                      readonly={readonly}
                      onRemove={(x) =>
                        onChange({
                          ...value,
                          safeties: safeties.filter(
                            (y) => y.safety_legacy_id !== x.safety_legacy_id,
                          ),
                        })
                      }
                    />
                  ))
                ) : (
                  <div style={{ fontSize: 11.5, color: "#A8AFBC", padding: "4px 0" }}>
                    Холбогдсон заавар байхгүй
                  </div>
                )}
                <AddByNo
                  kind="instruction"
                  disabled={readonly}
                  onAdd={(row) => {
                    if (
                      safeties.some((x) => x.safety_legacy_id === row.legacy_id)
                    ) {
                      message.info("Аль хэдийн нэмэгдсэн байна");
                      return;
                    }
                    onChange({
                      ...value,
                      safeties: [
                        ...safeties,
                        {
                          ...row,
                          safety_legacy_id: row.legacy_id,
                          is_auto: false,
                        },
                      ],
                    });
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {devices.length === 0 && !readonly && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#9AA3B0" }}>
          Тоног төхөөрөмж сонгоход технологийн карт болон ХАБЭА-н заавар
          автоматаар бөглөгдөнө.
        </div>
      )}
    </div>
  );
}
